import type { ElementType } from "react";
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import LogoutIcon from "@mui/icons-material/Logout";
import PersonIcon from "@mui/icons-material/Person";
import { useAppDispatch } from "@/hooks/useAppDispatch";
import { useAppSelector } from "@/hooks/useAppSelector";
import { logout } from "@/store/authSlice";
import { useActiveModule } from "@/hooks/useActiveModule";
import { moduleAdminPath } from "@/config/moduleDefaults";
import { resolveLogoutLoginPath } from "@/routes/ModuleScopeGuard";
import { UserAvatar } from "@/components/user/UserAvatar";
import { resolveProfileImageUrl } from "@/utils/fileUrl";
import { cn } from "@/utils/cn";

import type { ErpModule } from "@/api/platform.api";

interface ProfileMenuProps {
  variant?: "default" | "header";
}

type ProfileMenuActionTone = "profile" | "logout";

const MENU_ACTION_TONE: Record<
  ProfileMenuActionTone,
  { icon: ElementType; chip: string }
> = {
  profile: {
    icon: PersonIcon,
    chip: "bg-violet-100 text-violet-600 dark:bg-violet-950/70 dark:text-violet-300",
  },
  logout: {
    icon: LogoutIcon,
    chip: "bg-rose-100 text-rose-600 dark:bg-rose-950/70 dark:text-rose-300",
  },
};

function ProfileMenuActionIcon({ tone }: { tone: ProfileMenuActionTone }) {
  const { icon: Icon, chip } = MENU_ACTION_TONE[tone];
  return (
    <span
      className={cn(
        "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg",
        chip,
      )}
      aria-hidden
    >
      <Icon sx={{ fontSize: 18 }} />
    </span>
  );
}

function resolveProfilePath(
  masterAdmin?: boolean,
  activeModule?: ErpModule | null,
  entryModule?: ErpModule | null,
): string {
  if (masterAdmin) return "/master/profile";
  const module = activeModule ?? entryModule ?? "POS";
  return moduleAdminPath(module, "profile");
}

export function ProfileMenu({ variant = "default" }: ProfileMenuProps) {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAppSelector((s) => s.auth.user);
  const entryModule = useAppSelector((s) => s.auth.entryModule);
  const activeModule = useActiveModule();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const avatarUrl = useMemo(
    () =>
      resolveProfileImageUrl(
        user?.profilePictureUrl,
        user?.userUuid,
        user?.profilePictureRevision,
      ) ?? null,
    [user?.profilePictureUrl, user?.userUuid, user?.profilePictureRevision],
  );

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!user) return null;

  const fullName = `${user.firstName} ${user.lastName ?? ""}`.trim();
  const profilePath = resolveProfilePath(user.masterAdmin, activeModule, entryModule);

  const handleLogout = () => {
    setOpen(false);
    const loginPath = resolveLogoutLoginPath({
      pathname: location.pathname,
      entryModule,
      activeModule,
      masterAdmin: user.masterAdmin,
    });
    void dispatch(logout());
    navigate(loginPath, { replace: true });
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        className={cn(
          variant === "header"
            ? "workspace-header-profile-btn"
            : "inline-flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm font-medium text-slate-700 transition hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800",
        )}
        onClick={() => setOpen((value) => !value)}
        aria-label={t('labels.accountMenu')}
        aria-expanded={open}
      >
        <UserAvatar
          firstName={user.firstName}
          lastName={user.lastName}
          imageUrl={avatarUrl}
          size={variant === "header" ? "sm" : "md"}
        />
        {variant === "default" ? (
          <span className="hidden max-w-[10rem] truncate sm:inline">{fullName || user.email}</span>
        ) : null}
      </button>

      {open ? (
        <div
          className={cn(
            'absolute right-0 z-50 mt-2 w-56 animate-fade-in rounded-xl border border-slate-200 bg-white py-1 shadow-lg dark:border-slate-700 dark:bg-slate-900',
            variant === 'header' && 'workspace-header-overlay',
          )}
        >
          <div className="border-b border-slate-100 px-4 py-3 dark:border-slate-800">
            <p className="truncate text-sm font-semibold text-slate-900 dark:text-slate-100">
              {fullName || user.email}
            </p>
            <p className="truncate text-xs text-slate-500 dark:text-slate-400">{user.email}</p>
          </div>
          <div className="space-y-0.5 p-1">
            <button
              type="button"
              className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-medium text-slate-700 transition hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-800/80"
              onClick={() => {
                setOpen(false);
                navigate(profilePath);
              }}
            >
              <ProfileMenuActionIcon tone="profile" />
              {t('profileMenu.myProfile')}
            </button>
            <button
              type="button"
              className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-medium text-slate-700 transition hover:bg-rose-50/80 dark:text-slate-200 dark:hover:bg-rose-950/30"
              onClick={() => handleLogout()}
            >
              <ProfileMenuActionIcon tone="logout" />
              {t('profileMenu.signOut')}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
